import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import { createSherlookTheme } from './theme/createSherlookTheme';
import DashboardPage from './pages/DashboardPage';
import ProvidersPage from './pages/ProvidersPage';
import PartnerNavOrderPage from './pages/PartnerNavOrderPage';
import EditProviderPage from './pages/EditProviderPage';
import UsersPage from './pages/UsersPage';
import UserDetailsPage from './pages/UserDetailsPage';
import ProviderUsersPage from './pages/ProviderUsersPage';
import CategoriesPage from './pages/CategoriesPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProviderTypeManager from './components/ProviderTypeManager';
import StripeDashboardPage from './pages/StripeDashboardPage';
import DeliveryPricingPage from './pages/DeliveryPricingPage';
import MessagingSettingsPage from './pages/MessagingSettingsPage';
import QaLabPage from './pages/QaLabPage';
import CityTransportPage from './pages/CityTransportPage';
import ImageModerationAlertsPage from './pages/ImageModerationAlertsPage';
import HomeGoogleReviewsPage from './pages/HomeGoogleReviewsPage';
import HomeAnnouncementThemePage from './pages/HomeAnnouncementThemePage';
import HomeSplashScreenPage from './pages/HomeSplashScreenPage';
import HomeTickerAnnouncementsPage from './pages/HomeTickerAnnouncementsPage';
import FrontendUiSettingsPage from './pages/FrontendUiSettingsPage';
import MatchesAdminPage from './pages/MatchesAdminPage';
import AnnouncementsAdminPage from './pages/AnnouncementsAdminPage';
import AnnouncementDetailPage from './pages/AnnouncementDetailPage';
import MonitoringPage from './pages/MonitoringPage';
import PerformanceMonitoringPage from './pages/PerformanceMonitoringPage';
import PerformanceAlertsPage from './pages/PerformanceAlertsPage';
import UrlRedirectsPage from './pages/UrlRedirectsPage';
import SettingsPage from './pages/SettingsPage';
import SatisfactionEmailsPage from './pages/SatisfactionEmailsPage';
import SymfonyLogsPage from './pages/SymfonyLogsPage';
import PhpInfoPage from './pages/PhpInfoPage';
import DestroyPage from './pages/DestroyPage';

// Thème Sherlook aligné sur le dashboard prestataire
const theme = createSherlookTheme();

// Configuration de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/destroy" element={<DestroyPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/providers" element={<ProvidersPage />} />
                      <Route path="/providers/nav-order" element={<PartnerNavOrderPage />} />
                      <Route path="/providers/:id" element={<EditProviderPage />} />
                      <Route path="/providers/:id/edit" element={<EditProviderPage />} />
      <Route path="/provider-types" element={<ProviderTypeManager />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/users" element={<UsersPage />} />
      <Route path="/users/:id" element={<UserDetailsPage />} />
      <Route path="/provider-users" element={<ProviderUsersPage />} />
      <Route path="/statistics" element={<MonitoringPage />} />
      <Route path="/monitoring" element={<MonitoringPage />} />
      <Route path="/monitoring/performance" element={<PerformanceMonitoringPage />} />
      <Route path="/monitoring/alerts" element={<PerformanceAlertsPage />} />
                      <Route path="/stripe" element={<StripeDashboardPage />} />
                      <Route path="/delivery-pricing" element={<DeliveryPricingPage />} />
                      <Route path="/messaging-settings" element={<MessagingSettingsPage />} />
                      <Route path="/qa-lab" element={<QaLabPage />} />
                      <Route path="/satisfaction-emails" element={<SatisfactionEmailsPage />} />
                      <Route path="/city-transports" element={<CityTransportPage />} />
                      <Route path="/image-moderation-alerts" element={<ImageModerationAlertsPage />} />
                      <Route path="/home-google-reviews" element={<HomeGoogleReviewsPage />} />
                      <Route path="/home-announcement-theme" element={<HomeAnnouncementThemePage />} />
                      <Route path="/home-splash-screen" element={<HomeSplashScreenPage />} />
                      <Route path="/home-ticker-announcements" element={<HomeTickerAnnouncementsPage />} />
                      <Route path="/frontend-ui-settings" element={<FrontendUiSettingsPage />} />
                      <Route path="/matchings" element={<MatchesAdminPage />} />
                      <Route path="/announcements" element={<AnnouncementsAdminPage />} />
                      <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
                      <Route path="/url-redirects" element={<UrlRedirectsPage />} />
                      <Route path="/symfony-logs" element={<SymfonyLogsPage />} />
                      <Route path="/phpinfo" element={<PhpInfoPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/profile" element={<div>Mon profil</div>} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;

